import * as React from "react";
import { ChevronDown } from "lucide-react";

const SelectContext = React.createContext();

const Select = React.forwardRef(
  ({ children, value, onValueChange, ...props }, ref) => {
    const [open, setOpen] = React.useState(false);
    const [selectedValue, setSelectedValue] = React.useState(value);
    const selectRef = React.useRef(null);

    React.useEffect(() => {
      setSelectedValue(value);
    }, [value]);

    React.useEffect(() => {
      const handleClickOutside = (event) => {
        if (selectRef.current && !selectRef.current.contains(event.target)) {
          setOpen(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelect = (value) => {
      setSelectedValue(value);
      onValueChange?.(value);
      setOpen(false);
    };

    return (
      <SelectContext.Provider
        value={{ open, setOpen, selectedValue, handleSelect }}
      >
        <div ref={selectRef} className="relative w-full" {...props}>
          {children}
        </div>
      </SelectContext.Provider>
    );
  }
);
Select.displayName = "Select";

const SelectTrigger = React.forwardRef(
  ({ children, className, ...props }, ref) => {
    const { open, setOpen } = React.useContext(SelectContext);

    return (
      <button
        ref={ref}
        type="button"
        className={`flex justify-between items-center w-full px-3 py-2 text-sm 
          bg-white border border-orange-200 rounded-md hover:border-orange-300 
          focus:outline-none focus:ring-2 focus:ring-orange-200 
          disabled:cursor-not-allowed disabled:opacity-50
          transition-colors duration-200 ${className}`}
        onClick={() => setOpen(!open)}
        {...props}
      >
        {children}
        <ChevronDown
          className={`h-4 w-4 opacity-50 transition-transform duration-200 
          ${open ? "transform rotate-180" : ""}`}
        />
      </button>
    );
  }
);
SelectTrigger.displayName = "SelectTrigger";

const SelectValue = React.forwardRef(
  ({ children, className, placeholder, ...props }, ref) => {
    const { selectedValue } = React.useContext(SelectContext);

    return (
      <span
        ref={ref}
        className={`block truncate ${
          !selectedValue ? "text-gray-400" : ""
        } ${className}`}
        {...props}
      >
        {selectedValue || placeholder}
      </span>
    );
  }
);
SelectValue.displayName = "SelectValue";

const SelectContent = React.forwardRef(
  ({ children, className, ...props }, ref) => {
    const { open } = React.useContext(SelectContext);

    if (!open) return null;

    return (
      <div
        ref={ref}
        className={`absolute z-50 w-full mt-1 py-1 bg-white border border-orange-200 
          rounded-md shadow-lg overflow-hidden
          animate-in fade-in-0 zoom-in-95
          data-[side=bottom]:slide-in-from-top-2
          data-[side=top]:slide-in-from-bottom-2 ${className}`}
        {...props}
      >
        {children}
      </div>
    );
  }
);
SelectContent.displayName = "SelectContent";

const SelectItem = React.forwardRef(
  ({ children, className, value, ...props }, ref) => {
    const { selectedValue, handleSelect } = React.useContext(SelectContext);
    const isSelected = selectedValue === value;

    return (
      <div
        ref={ref}
        role="option"
        aria-selected={isSelected}
        className={`px-2 py-1.5 text-sm cursor-pointer select-none
          hover:bg-orange-50 hover:text-orange-600
          ${isSelected ? "bg-orange-50 text-orange-600" : ""}
          ${className}`}
        onClick={() => handleSelect(value)}
        {...props}
      >
        {children}
      </div>
    );
  }
);
SelectItem.displayName = "SelectItem";

const SelectComponent = () => null;

export {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectComponent,
};
